import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a WhatsApp order link pre-filled with the
 * product name and currently selected variant. Listens for variant change
 * events so the message stays in sync without a page reload.
 *
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement} link
 *
 * @extends {Component<Refs>}
 */
class WhatsappOrderButton extends Component {
  requiredRefs = ['link'];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource, productId } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (productId && productId !== this.dataset.productId) {
          return;
        }

        if (!resource) return;

        this.#updateLink(resource.title);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[whatsapp-order-button] Event promise rejected:', error);
      });
  };

  /**
   * @param {string} [variantTitle]
   */
  #updateLink(variantTitle) {
    const { link } = this.refs;
    if (!link) return;

    const phone = this.dataset.phone || '';
    const productTitle = this.dataset.productTitle || '';
    const hasVariant = variantTitle && variantTitle !== 'Default Title';

    const message = hasVariant
      ? `I want to purchase ${productTitle} - ${variantTitle}`
      : `I want to purchase ${productTitle}`;

    link.setAttribute('href', `https://wa.me/${phone}?text=${encodeURIComponent(message)}`);
  }
}

if (!customElements.get('whatsapp-order-button')) {
  customElements.define('whatsapp-order-button', WhatsappOrderButton);
}
